import React, { useEffect, useState } from "react";
import { Clock, LogIn, LogOut } from "lucide-react";
import { useAuth } from "../Context/AuthContext";
import "../Styling/WorkHours.css";
import { Base_URL } from "../url";

const WorkHours = ({ attendanceUpdated }) => {
  const { user } = useAuth();
  const userId = user?.userId;

  const [punchInTime, setPunchInTime] = useState(null);
  const [punchOutTime, setPunchOutTime] = useState(null);
  const [workingTime, setWorkingTime] = useState("00 : 00");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!userId) return;

    const today = new Date().toISOString().split("T")[0];

    const fetchPunchTimes = async () => {
      try {
        setLoading(true);
        setError(false);

        const res = await fetch(
          `${Base_URL}/Attendance/GetPunchInTime/${userId}?date=${today}`
        );

        if (!res.ok) {
          throw new Error("Failed to fetch punch times");
        }

        const data = await res.json();

        setPunchInTime(
          data?.punchInTime && data.punchInTime !== "00:00:00"
            ? data.punchInTime
            : null
        );

        setPunchOutTime(
          data?.punchOutTime && data.punchOutTime !== "00:00:00"
            ? data.punchOutTime
            : null
        );
      } catch (err) {
        console.error(err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchPunchTimes();
  }, [userId, attendanceUpdated]);

 
  useEffect(() => {
    if (!punchInTime) {
      setWorkingTime("00 : 00");
      return;
    }

    const buildDateFromTime = (time) => {
      const [h, m, s] = time.split(":");
      const date = new Date();
      date.setHours(h, m, s || 0, 0);
      return date;
    };

    const punchInDate = buildDateFromTime(punchInTime);
    const punchOutDate = punchOutTime
      ? buildDateFromTime(punchOutTime)
      : null;

    const calculateWorkingTime = () => {
      const endTime = punchOutDate || new Date();
      const diffMs = endTime - punchInDate;

      if (diffMs <= 0) return;

      const totalMinutes = Math.floor(diffMs / 60000);
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;

      setWorkingTime(
        `${String(hours).padStart(2, "0")} : ${String(minutes).padStart(2, "0")}`
      );
    };

    calculateWorkingTime();

    if (!punchOutTime) {
      const intervalId = setInterval(calculateWorkingTime, 60000);
      return () => clearInterval(intervalId);
    }
  }, [punchInTime, punchOutTime]);

  
  const formatTime = (time) => {
    if (!time) return "—";
    const [h, m] = time.split(":");
    const hour = Number(h);
    const period = hour >= 12 ? "PM" : "AM";
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${m} ${period}`;
  };

  return (
    <div className="workhours-card">
      {/* Punch In Time */}
      <div className="workhours-row">
        <div className="workhours-icon">
          <LogIn size={20} />
        </div>
        <div className="workhours-text">
          <h4>Punch In Time</h4>
          <p>Today’s check-in</p>
        </div>
        <div className="workhours-value">
          {loading ? (
            <span className="muted">Loading...</span>
          ) : error ? (
            <span className="error">N/A</span>
          ) : punchInTime ? (
            <span className="punch-time">{formatTime(punchInTime)}</span>
          ) : (
            <span className="muted">Not punched in</span>
          )}
        </div>
      </div>

      {/* Punch Out Time */}
      <div className="workhours-row" style={{ marginTop: "16px" }}>
        <div className="workhours-icon">
          <LogOut size={20} />
        </div>
        <div className="workhours-text">
          <h4>Punch Out Time</h4>
          <p>Today’s check-out</p>
        </div>
        <div className="workhours-value">
          {punchOutTime ? (
            <span className="punch-time">{formatTime(punchOutTime)}</span>
          ) : (
            <span className="muted wrap-text">N/A</span>
          )}
        </div>
      </div>

      {/* Working Hours */}
      <div className="workhours-row" style={{ marginTop: "16px" }}>
        <div className="workhours-icon">
          <Clock size={20} />
        </div>
        <div className="workhours-text">
          <h4>
            {punchOutTime ? "Today’s Worked Hours" : "Current Working Hours"}
          </h4>
          <p>Auto calculated</p>
        </div>
        <div className="workhours-value">
          {punchInTime ? (
            <span>{workingTime}</span>
          ) : (
            <span className="muted">00 : 00</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkHours;
